/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import { MarketingSectionHeader } from "@/components/landing/marketing-section-header";
import { PricingCards } from "@/components/landing/pricing-cards";
import { PricingComparisonTable } from "@/components/landing/pricing-comparison-table";
import { EMAILS } from "@/lib/constants/master";
import { pricingPlans } from "@/content/landing";
import MainNavigation from "@/components/layout/main-navigation";
import {
  StructuredData,
  formatFAQForStructuredData,
} from "@/components/seo/structured-data";
import { CheckCircle } from "lucide-react";
import { FadeIn } from "@/components/landing/primitives/fade-in";

export function PricingPageClient() {
  const [isAnnual, setIsAnnual] = useState(false);

  const faqs = [
    {
      question: "Is there a free trial?",
      answer:
        "Download the mobile app for 5 free AI Quick Estimates — no signup, no card. To save estimates, send proposals, or access the web platform, pick any paid plan. Cancel anytime.",
    },
    {
      question: "Do I need to build my own cost database on Starter?",
      answer:
        "No. Starter prices jobs from your labor rate + burden plus AI pricing search. Upgrade to Professional when you want to own your cost data with a pre-loaded database for materials, equipment, and labor.",
    },
    {
      question: "What happens if I hit my AI Quick Estimate limit?",
      answer:
        "Starter: we'll prompt you to upgrade. Professional: soft cap with a short pause before it resets. Business: unlimited under fair use.",
    },
    {
      question: "Can I change my plan later?",
      answer:
        "Yes, upgrade or downgrade anytime. Changes take effect immediately and are prorated.",
    },
    {
      question: "Is pricing per user or per company?",
      answer:
        "Per user, per month. Mobile and Starter are solo plans. Professional and Business include office seats + field seats for your crew.",
    },
    {
      question: "What about the cost database — is it in every plan?",
      answer:
        "The pre-loaded cost database (materials, equipment, labor scaffolding for 8 trades) is a Professional feature. Mobile and Starter use AI pricing search instead so you can start without building a catalog.",
    },
    {
      question: "Do you offer annual billing?",
      answer: "Yes. Annual plans save ~15% — billed yearly instead of monthly.",
    },
    {
      question: "Can I cancel anytime?",
      answer:
        "Yes. No long-term contracts or cancellation fees. Cancel from your account settings.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <StructuredData
        type="faq"
        data={{ questions: formatFAQForStructuredData(faqs) }}
      />
      <StructuredData type="software" />
      <MainNavigation />

      {/* Hero */}
      <MarketingPageHero
        variant="dark"
        badge="Pricing"
        title={
          <>
            Plans that pay for themselves{" "}
            <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
              with one won bid
            </span>
          </>
        }
        description="Every plan starts with a 7-day free trial. Full access. No credit card required."
      />

      {/* Pricing Toggle & Cards */}
      <section
        className="py-16 md:py-24 lg:py-32 bg-background"
        aria-labelledby="pricing-plans"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          {/* Trial Banner */}
          <FadeIn className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary/10 border border-secondary/20 px-4 py-2 text-sm font-medium text-secondary">
              All plans include a 7-day free trial — full access, cancel anytime
            </div>
          </FadeIn>

          {/* Pricing Toggle */}
          <FadeIn className="mb-16 flex justify-center">
            <div
              className="flex items-center space-x-2 rounded-xl p-1 bg-muted border border-border"
              role="radiogroup"
              aria-label="Billing cycle selection"
            >
              <button
                onClick={() => setIsAnnual(false)}
                role="radio"
                aria-checked={!isAnnual}
                className={`rounded-lg px-4 py-3 text-sm font-semibold transition-all ${
                  !isAnnual
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setIsAnnual(true)}
                role="radio"
                aria-checked={isAnnual}
                className={`rounded-lg px-4 py-3 text-sm font-semibold transition-all ${
                  isAnnual
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Annual
                <span className="ml-2 rounded-full bg-secondary/10 text-secondary px-2 py-0.5 text-xs font-bold">
                  Save 10%
                </span>
              </button>
            </div>
          </FadeIn>

          <PricingCards plans={pricingPlans} isAnnual={isAnnual} />
        </div>
      </section>

      {/* Comparison Table */}
      <PricingComparisonTable />

      {/* FAQ Section */}
      <section
        className="py-16 md:py-24 lg:py-32 bg-background-warm"
        aria-labelledby="frequently-asked-questions"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          <MarketingSectionHeader
            title="Frequently asked questions"
            description="Everything you need to know about our pricing and plans"
          />

          <dl className="max-w-4xl mx-auto grid gap-6 md:grid-cols-2">
            {faqs.map((faq, index) => (
              <FadeIn
                key={faq.question}
                className="card-premium rounded-xl border border-border/60 bg-card p-5"
                delay={index * 100}
              >
                <dt className="text-lg font-semibold text-foreground flex items-start gap-2">
                  <CheckCircle
                    className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  {faq.question}
                </dt>
                <dd className="text-muted-foreground leading-relaxed pl-7 mt-3">
                  {faq.answer}
                </dd>
              </FadeIn>
            ))}
          </dl>
        </div>
      </section>

      {/* CTA */}
      <MarketingCTASection
        title="Start your free trial"
        description="7 days of full access. No credit card required. Pick your trade and start estimating."
        primaryCTA={{
          text: "Start Your Free Trial",
          href: "/auth/sign-up",
        }}
        secondaryCTA={{
          text: "Contact Us",
          href: `mailto:${EMAILS.CONTACT}`,
        }}
        trustIndicators={[
          "7-day free trial • No credit card required • Cancel anytime",
        ]}
      />

      <Footer />
    </div>
  );
}
